import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Typography } from "@mui/material";
import { Drawer, Select, TextField, toast } from "@outpave/ds";
import type {
  CreateWebhookEndpointInput,
  UpdateWebhookEndpointInput,
  WebhookEndpoint,
  WebhookEndpointWithSecret,
} from "hooks/useWebhookEndpoints";
import { useUnsavedChangesGuard } from "hooks/useUnsavedChangesGuard";
import { toastMutationError } from "utils/toastMutationError";
import {
  hasWebhookEndpointChanges,
  isHttpsUrl,
  sameWebhookEvents,
  webhookEventOptions,
  type WebhookEndpointForm,
} from "utils/webhooks/webhookEndpoints";

interface WebhookEndpointDrawerProps {
  open: boolean;
  // The endpoint to edit; null adds a new one.
  endpoint: WebhookEndpoint | null;
  eventTypes: string[];
  onClose: () => void;
  onCreate: (
    input: CreateWebhookEndpointInput,
  ) => Promise<WebhookEndpointWithSecret>;
  onUpdate: (
    id: string,
    input: UpdateWebhookEndpointInput,
  ) => Promise<WebhookEndpoint>;
  // A new endpoint's secret goes to the page, which shows it once.
  onSecretReveal: (result: WebhookEndpointWithSecret) => void;
}

const EMPTY_FORM: WebhookEndpointForm = { url: "", events: [] };

const WebhookEndpointDrawer: React.FC<WebhookEndpointDrawerProps> = ({
  open,
  endpoint,
  eventTypes,
  onClose,
  onCreate,
  onUpdate,
  onSecretReveal,
}) => {
  const { t } = useTranslation();
  const initial = useMemo<WebhookEndpointForm>(
    () =>
      endpoint ? { url: endpoint.url, events: endpoint.events } : EMPTY_FORM,
    [endpoint],
  );
  const [url, setUrl] = useState(initial.url);
  const [events, setEvents] = useState<string[]>(initial.events);
  const [urlTouched, setUrlTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUrl(initial.url);
    setEvents(initial.events);
    setUrlTouched(false);
    setSubmitting(false);
  }, [open, initial]);

  const current = { url, events };
  const hasChanges = hasWebhookEndpointChanges(initial, current);
  const urlValid = isHttpsUrl(url);
  const canSubmit =
    !submitting &&
    urlValid &&
    events.length > 0 &&
    (endpoint === null || hasChanges);

  const { requestLeave, dialog } = useUnsavedChangesGuard(hasChanges, onClose);

  // The X, backdrop, Escape and Cancel all leave here.
  const handleClose = () => {
    if (submitting) return;
    requestLeave();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      if (endpoint) {
        await onUpdate(endpoint.id, {
          ...(url.trim() === initial.url ? {} : { url: url.trim() }),
          ...(sameWebhookEvents(initial.events, events) ? {} : { events }),
          revision: endpoint.revision,
        });
        toast.success(t("WebhookEndpointUpdated"));
      } else {
        onSecretReveal(await onCreate({ url: url.trim(), events }));
      }
      onClose();
    } catch (err: unknown) {
      toastMutationError(
        err,
        endpoint
          ? t("WebhookEndpointUpdateError")
          : t("WebhookEndpointCreateError"),
      );
      setSubmitting(false);
    }
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={handleClose}
        title={endpoint ? t("Edit endpoint") : t("Add endpoint")}
        loading={submitting}
        footer={
          <>
            <Button
              variant="primary-outlined"
              onClick={handleClose}
              disabled={submitting}
            >
              {t("Cancel")}
            </Button>
            <Button
              variant="primary-filled"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {endpoint ? t("Save changes") : t("Add endpoint")}
            </Button>
          </>
        }
      >
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("WebhookEndpointDrawerDescription")}
        </Typography>

        <TextField
          label={t("Endpoint URL")}
          placeholder="https://example.com/webhooks"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onBlur={() => setUrlTouched(true)}
          errorText={
            urlTouched && !urlValid ? t("WebhookUrlInvalid") : undefined
          }
          helperText={t("WebhookUrlHelper")}
          fullWidth
          required
          disabled={submitting}
        />

        <Select
          multiple
          label={t("Events")}
          placeholder={t("WebhookEventsPlaceholder")}
          value={events}
          onChange={setEvents}
          options={webhookEventOptions(eventTypes)}
          noOptionsText={t("WebhookNoEventTypes")}
          helperText={
            eventTypes.length === 0
              ? t("WebhookNoEventTypes")
              : t("WebhookEventsHelper")
          }
          displayMultipleUnder
          required
          fullWidth
          disabled={submitting}
        />
      </Drawer>
      {dialog}
    </>
  );
};

export default WebhookEndpointDrawer;
